import type { QueryClient } from "@tanstack/react-query";
import { financeDashboardKeys } from "@/features/finance/hooks/query-keys";
import { accountKeys } from "@/features/accounts/hooks/query-keys";
import { creditKeys } from "@/features/credits/hooks/query-keys";
import { estimateKeys } from "@/features/estimates/hooks/query-keys";
import { invoiceKeys } from "@/features/invoices/hooks/query-keys";
import { dashboardKeys } from "@/features/dashboard/hooks/query-keys";

/**
 * Every credit movement — applying a credit, reversing that application, or
 * minting one from an overpayment — moves the same rows: the credit's available
 * balance, the invoice or estimate whose balance it settled, the owning account's
 * credit balance, and both dashboards built from those rows. `dashboardKeys`
 * (`["dashboard"]`) does not prefix-match `financeDashboardKeys`
 * (`["finance-dashboard"]`), so both have to be listed.
 *
 * Every hook that owns one of those writes refreshes through here so the sets
 * cannot drift apart: a caller that refreshes one surface but not the others
 * leaves the operator staring at a pre-write number on a write the UI has
 * already confirmed.
 */
export async function invalidateCreditApplicationQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: creditKeys.all }),
    queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
    queryClient.invalidateQueries({ queryKey: estimateKeys.all }),
    queryClient.invalidateQueries({ queryKey: accountKeys.all }),
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
    queryClient.invalidateQueries({ queryKey: financeDashboardKeys.all }),
  ]);
}
